// src/sectors/pilates/sessions/components/calendar/utils.js

export function normISO(x) {
  return String(x || '').slice(0, 10);
}

/* ---------------- UI normalize (STANDARD) ---------------- */
const clean = (x) => {
  const s = (x ?? '').toString().trim().replace(/\s+/g, ' ');
  return s.length ? s : '';
};

export function normalizeSessionForUI(raw, trainersById) {
  const s = raw || {};
  const tr = trainersById?.get?.(String(s.trainerId));

  const dayISO = normISO(s.dayISO || s.day || s.date);
  const startMin = Number.isFinite(s.startMin) ? s.startMin : Number(s.startMin);
  const safeStartMin = Number.isFinite(startMin) ? startMin : 0;

  const activityName =
    clean(s.activityNameSnapshot) || clean(s.activityName) || clean(s.title) || 'Seans';

  const trainerName =
    clean(s.trainerNameSnapshot) || clean(s.trainerName) || clean(tr?.name) || '—';

  const members = Array.isArray(s.participants)
    ? s.participants
        .map((p) => clean(p?.fullName || p?.memberNameSnapshot || p?.memberName))
        .filter(Boolean)
    : Array.isArray(s.members)
    ? s.members.map((x) => clean(x)).filter(Boolean)
    : [];

  const capacity = Number.isFinite(s.capacity) ? s.capacity : Number.isFinite(s.cap) ? s.cap : 1;

  const participantCount = Number.isFinite(s.participantCount)
    ? s.participantCount
    : Number.isFinite(s.booked)
    ? s.booked
    : members.length;

  return {
    ...s,
    id: s.id ?? s.sessionId,
    dayISO,
    startMin: safeStartMin,
    activityName,
    trainerName,
    members,
    capacity,
    participantCount,
    sessionType: s.sessionType || s.type || 'NORMAL',
  };
}

export function sessionKey(s) {
  const id = s?.id ?? s?.sessionId;
  if (id != null) return String(id);

  const day = normISO(s?.dayISO || s?.day || s?.date);
  const start = Number.isFinite(s?.startMin) ? s.startMin : Number(s?.startMin) || 0;
  const t = s?.trainerId ?? 'x';
  const a = s?.activityId ?? s?.activityName ?? s?.title ?? 'a';
  return `${day}|${start}|${t}|${a}`;
}

/* ---------------- Calendar tools -> config ---------------- */
export function toolsToCalendarConfig(tools) {
  const t = tools || {};
  const calendarType = String(t.calendarType || 'TAM_SAAT');

  const dayStartHour = Number(t.dayStartHour);
  const dayEndHour = Number(t.dayEndHour);

  const start = Number.isFinite(dayStartHour) ? Math.max(0, Math.min(23, dayStartHour)) : 8;
  const end = Number.isFinite(dayEndHour) ? Math.max(1, Math.min(24, dayEndHour)) : 22;

  const safeEnd = end <= start ? Math.min(24, start + 1) : end;

  const { stepMin, offsetMin } = calendarGridByType(calendarType);

  return {
    calendarType,
    dayStartHour: start,
    dayEndHour: safeEnd,
    stepMin,
    offsetMin,
  };
}

export function buildTimeSlots(cfg) {
  const c = cfg || {};
  const start = Number(c.dayStartHour ?? 8);
  const end = Number(c.dayEndHour ?? 22);
  const step = Number(c.stepMin ?? 60);
  const offset = Number(c.offsetMin ?? 0);

  const startMin = start * 60 + offset;
  const endMinExclusive = end * 60 + offset;

  const out = [];
  for (let m = startMin; m < endMinExclusive; m += step) {
    const hh = String(Math.floor(m / 60)).padStart(2, '0');
    const mm = String(m % 60).padStart(2, '0');
    out.push({
      minutes: m,
      label: `${hh}:${mm}`,
      hour: Math.floor(m / 60),
      minute: m % 60,
    });
  }
  return out;
}

/* ---------------- ✅ Grid definition by calendarType ---------------- */
/**
 * TAM_SAAT:      step=60, offset=0   => 12:00, 13:00...
 * YARIM_SAAT_TAM step=60, offset=30  => 12:30, 13:30...
 * YARIM_SAAT_YARIM step=30, offset=30 => 12:30, 13:00, 13:30...
 */
export function calendarGridByType(calendarType) {
  const t = String(calendarType || 'TAM_SAAT');

  if (t === 'YARIM_SAAT_TAM') return { stepMin: 60, offsetMin: 30 };
  if (t === 'YARIM_SAAT_YARIM') return { stepMin: 30, offsetMin: 30 };

  return { stepMin: 60, offsetMin: 0 };
}

/* ---------------- ✅ SLOT ROUNDING (DEFAULT: FLOOR / aşağı) ---------------- */
/**
 * Offset-aware aşağı yuvarlama:
 * - YARIM_* (offset=30) iken 12:00 -> 11:30
 * - TAM_SAAT (offset=0) iken 12:30 -> 12:00
 */
export function roundStartMinToCalendarType(startMin, calendarType) {
  const m = Number(startMin);
  if (!Number.isFinite(m)) return startMin;

  const { stepMin, offsetMin } = calendarGridByType(calendarType);

  // floor((m - offset)/step)*step + offset
  const shifted = m - offsetMin;
  const floored = Math.floor(shifted / stepMin) * stepMin;
  return floored + offsetMin;
}

/* ---------------- Map builder ---------------- */
/**
 * UI'da kart kaybolmasın diye, seansı grid'e koyarken startMin'i yuvarlarız.
 * DB'yi değiştirmez, sadece UI key'ini düzeltir.
 */
export function buildByDaySlot(sessions, calendarType = 'TAM_SAAT') {
  const map = new Map();

  (sessions || []).forEach((raw) => {
    const day = normISO(raw?.dayISO || raw?.day || raw?.date);

    const rawStart = Number(raw?.startMin);
    if (!day || !Number.isFinite(rawStart)) return;

    const start = roundStartMinToCalendarType(rawStart, calendarType);

    const key = `${day}|${start}`;
    if (!map.has(key)) map.set(key, []);

    map.get(key).push({
      ...raw,
      dayISO: day,
      startMin: start, // ✅ UI slot key
      _rawStartMin: rawStart, // debug/ileride migrate için
    });
  });

  for (const [k, arr] of map.entries()) {
    arr.sort((a, b) => String(a.trainerId ?? '').localeCompare(String(b.trainerId ?? '')));
    map.set(k, arr);
  }

  return map;
}
